"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnstyledDateInput = exports.DateInput = exports.DateSegment = exports.DateField = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../../theme");
/**
 * A date field allows users to enter and edit date and time values using a keyboard.
 * Each part of a date value (day, month, year, etc.) is editable, and can be incremented and decremented using arrow keys.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/DateField.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-datefield--default}
 *
 * @example
 * ```tsx
 * <DateField>
 *   <DateInput>
 *     <DateSegment segment="month" />
 *     <DateSegment segment="day" />
 *     <DateSegment segment="year" />
 *   </DateInput>
 * </DateField>
 * ```
 *
 * @param value - The current value (controlled)
 * @param defaultValue - The default value (uncontrolled)
 * @param isDisabled - Whether the input is disabled
 * @param isReadOnly - Whether the input can be selected but not changed by the user
 * @param isRequired - Whether user input is required on the input before form submission
 * @param minValue - The minimum allowed date value
 * @param maxValue - The maximum allowed date value
 * @param placeholderValue - A placeholder date that influences the format of the placeholder shown when no value is selected
 * @param children - The date input components to display
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.DateField = (0, styled_1.default)(react_aria_components_1.DateField)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"])), theme_1.theme.space.space8);
/**
 * A DateSegment renders an individual segment.
 *
 * @example
 * ```tsx
 * {segment => <DateSegment segment={segment} />}
 * ```
 *
 * @param segment - The date segment to render
 */
exports.DateSegment = (0, styled_1.default)(react_aria_components_1.DateSegment)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  padding: ", ";\n  text-align: end;\n  color: ", ";\n\n  &[data-placeholder] {\n    color: ", ";\n  }\n\n  &:focus {\n    color: ", ";\n    background: ", ";\n    outline: none;\n    border-radius: ", ";\n    caret-color: transparent;\n  }\n"], ["\n  padding: ", ";\n  text-align: end;\n  color: ", ";\n\n  &[data-placeholder] {\n    color: ", ";\n  }\n\n  &:focus {\n    color: ", ";\n    background: ", ";\n    outline: none;\n    border-radius: ", ";\n    caret-color: transparent;\n  }\n"])), theme_1.theme.space.space2, theme_1.theme.colors.n900, theme_1.theme.colors.n500, theme_1.theme.colors.white, theme_1.theme.colors.blue500, theme_1.theme.radii.rounded);
/**
 * A date input groups multiple date segments together to form a complete date value.
 * It handles keyboard navigation between segments and provides a consistent layout.
 *
 * @example
 * ```tsx
 * <DateInput>
 *   <DateSegment segment="month" />
 *   <DateSegment segment="day" />
 *   <DateSegment segment="year" />
 * </DateInput>
 * ```
 *
 * @param isDisabled - Whether the date input is disabled
 * @param isReadOnly - Whether the date input is read only
 * @param isRequired - Whether the date input is required
 * @param isInvalid - Whether the date input is invalid
 * @param children - The date segments to display
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.DateInput = (0, styled_1.default)(react_aria_components_1.DateInput)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  border: ", " solid ", ";\n  padding: ", " ", ";\n  display: flex;\n  box-sizing: border-box;\n  height: 40px;\n  min-width: 200px;\n  max-width: 560px;\n  border-radius: ", ";\n  outline: none;\n\n  &[data-disabled] {\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-hovered] {\n    border-color: ", ";\n  }\n\n  &[data-invalid] {\n    border-color: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"], ["\n  border: ", " solid ", ";\n  padding: ", " ", ";\n  display: flex;\n  box-sizing: border-box;\n  height: 40px;\n  min-width: 200px;\n  max-width: 560px;\n  border-radius: ", ";\n  outline: none;\n\n  &[data-disabled] {\n    opacity: ", ";\n    cursor: not-allowed;\n  }\n\n  &[data-hovered] {\n    border-color: ", ";\n  }\n\n  &[data-invalid] {\n    border-color: ", ";\n  }\n\n  &[data-focus-visible] {\n    outline: ", " solid ", ";\n    outline-offset: ", ";\n  }\n"])), theme_1.theme.borderWidths.regular, theme_1.theme.colors.n80, theme_1.theme.space.space8, theme_1.theme.space.space12, theme_1.theme.radii.field, theme_1.theme.opacity.semiOpaque, theme_1.theme.colors.n200, theme_1.theme.colors.red500, theme_1.theme.borderWidths.thick, theme_1.theme.colors.blue400, theme_1.theme.space.space2);
/**
 * An unstyled date input that provides a minimal layout for date segments.
 * Useful when you want to customize the appearance of the date input completely.
 *
 * @example
 * ```tsx
 * <UnstyledDateInput>
 *   <DateSegment segment="month" />
 *   <DateSegment segment="day" />
 *   <DateSegment segment="year" />
 * </UnstyledDateInput>
 * ```
 *
 * @param isDisabled - Whether the date input is disabled
 * @param isReadOnly - Whether the date input is read only
 * @param isRequired - Whether the date input is required
 * @param isInvalid - Whether the date input is invalid
 * @param children - The date segments to display
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.UnstyledDateInput = (0, styled_1.default)(react_aria_components_1.DateInput)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  border: none;\n  outline: none;\n\n  &[slot='start'] {\n    flex: 0;\n  }\n\n  &[slot='start'] + span {\n    padding: 0 ", ";\n    flex: 0;\n  }\n\n  &[slot='end'] {\n    margin-right: ", ";\n    flex: 1;\n  }\n"], ["\n  display: flex;\n  border: none;\n  outline: none;\n\n  &[slot='start'] {\n    flex: 0;\n  }\n\n  &[slot='start'] + span {\n    padding: 0 ", ";\n    flex: 0;\n  }\n\n  &[slot='end'] {\n    margin-right: ", ";\n    flex: 1;\n  }\n"])), theme_1.theme.space.space4, theme_1.theme.space.space32);
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=DateField.js.map